import { RESOURCES_NAMES } from "@/const/resource.constants"
import {
  Config,
  Params,
  predicateHelpers,
  useCreate,
  useDelete,
  useHttp
} from "@/hooks/useHttp"
import { AuthKeyTypeValues } from "./useAuthKeys.selector"
import {
  AuthKeysParams,
  AuthKeysResponse,
  AuthKeyType,
  FilterAuthKeysResponse
} from "./useAuthKeys.types"

import type { GetUseMutationOptions } from "@/hooks/useHttp.types"

const NULL_PARAMS = undefined // used to make sure the urls share the same key for refresh

// a created or deleted key changes the lists, whatever their filters, and the creators they're filtered by
const refreshKeys =
  <T, S>(needle: AuthKeyType): GetUseMutationOptions<T, S> =>
  (queryClient) => ({
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: predicateHelpers.includesKeysPredicate([
          AuthKeyTypeValues[needle]
        ])
      })
  })

export const useGetAuthKeys = <T = AuthKeysResponse, S = T>(
  needle: AuthKeyType,
  params?: AuthKeysParams,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(AuthKeyTypeValues[needle], params, config)

  return context
}

export const useGetFiltersAuthKeys = <T = FilterAuthKeysResponse, S = T>(
  needle: AuthKeyType,
  params: Params = {},
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${AuthKeyTypeValues[needle]}/filters`,
    params,
    config
  )

  return context
}

export const useCreateScimAuthKey = <T = any, S = T>() => {
  const context = useCreate<T, S>(
    RESOURCES_NAMES.AUTHENTICATION.SCIM_KEY,
    NULL_PARAMS
  )

  return context
}

export const useCreateKey = (needle: AuthKeyType) => {
  const context = useCreate<any, AuthKeysResponse>(
    AuthKeyTypeValues[needle],
    NULL_PARAMS,
    undefined,
    refreshKeys(needle)
  )

  return context
}

export const useDeleteKey = <T = unknown>(needle: AuthKeyType) => {
  const context = useDelete<T>(
    AuthKeyTypeValues[needle],
    NULL_PARAMS,
    undefined,
    refreshKeys(needle)
  )

  return context
}
